import {
  getClientCompanyId,
  getDeviceUuid,
  getKioskEmployeeId,
} from "./auth-context";

export type DeviceType = "WEB" | "APP" | "KIOSKO";

export interface DeviceHeaderOptions {
  clientCompanyId?: string | null;
  deviceUuid?: string | null;
  kioskEmployeeId?: string | null;
}

export interface RuntimeDeviceSnapshot {
  appVersion: string | null;
  deviceUuid: string | null;
  isApp: boolean;
  isMobile: boolean;
  isPhone: boolean;
  isTablet: boolean;
  locale: string | null;
  notificationToken: string | null;
  platform: string;
}

let lastKnownPosition: { latitude: number; longitude: number } | null = null;

function readWindowLastKnownPosition(): {
  latitude: number;
  longitude: number;
} | null {
  if (!canUseWindow()) {
    return null;
  }

  const position = window.__wktLastKnownPosition;
  if (
    !position ||
    !Number.isFinite(position.latitude) ||
    !Number.isFinite(position.longitude)
  ) {
    return null;
  }

  return {
    latitude: position.latitude,
    longitude: position.longitude,
  };
}

function normalizeRuntimeValue(value?: string | null): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function canUseWindow(): boolean {
  return typeof window !== "undefined";
}

function getUrlParameter(name: string): string | null {
  if (!canUseWindow()) {
    return null;
  }

  const value = new URLSearchParams(window.location.search).get(name);
  return normalizeRuntimeValue(value);
}

function getUserAgent(): string {
  if (!canUseWindow()) {
    return "";
  }

  return navigator.userAgent || "";
}

function hasTouchPoints(): boolean {
  if (!canUseWindow()) {
    return false;
  }

  return navigator.maxTouchPoints > 0;
}

function isMobileUserAgent(): boolean {
  return /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(
    getUserAgent(),
  );
}

function isTabletUserAgent(): boolean {
  return /iPad|Tablet|PlayBook|Silk/i.test(getUserAgent());
}

function isPhoneUserAgent(): boolean {
  return isMobileUserAgent() && !isTabletUserAgent();
}

export function isAppRuntime(): boolean {
  if (!canUseWindow()) {
    return false;
  }

  if (getUrlParameter("cordova")) {
    return true;
  }

  const nativePlatform = normalizeRuntimeValue(window.deviceNativePlatform);
  if (nativePlatform && nativePlatform !== "browser") {
    return true;
  }

  return false;
}

export function getRuntimePlatform(): string {
  const platform =
    canUseWindow() ? normalizeRuntimeValue(window.deviceNativePlatform) : null;

  if (platform) {
    return platform;
  }

  return isAppRuntime() ? "app" : "browser";
}

export function isMobileRuntime(): boolean {
  return hasTouchPoints() || isMobileUserAgent();
}

export function isTabletRuntime(): boolean {
  return isTabletUserAgent();
}

export function isPhoneRuntime(): boolean {
  return isPhoneUserAgent();
}

export function getNotificationToken(): string | null {
  if (!canUseWindow()) {
    return null;
  }

  return normalizeRuntimeValue(window.deviceNotificationToken);
}

export function getAppVersion(): string | null {
  if (!canUseWindow()) {
    return null;
  }

  return (
    normalizeRuntimeValue(window.deviceCordovaWrapperVersion) ??
    getUrlParameter("cordova")
  );
}

export function getRuntimeLocale(): string | null {
  if (!canUseWindow()) {
    return null;
  }

  return normalizeRuntimeValue(
    window.deviceLocale ??
      (navigator.languages !== undefined
        ? navigator.languages[0]
        : navigator.language),
  );
}

export function getRuntimeVersion(): string | null {
  return getAppVersion();
}

export function getRuntimeDeviceSnapshot(): RuntimeDeviceSnapshot {
  return {
    appVersion: getAppVersion(),
    deviceUuid: getDeviceUuid(),
    isApp: isAppRuntime(),
    isMobile: isMobileRuntime(),
    isPhone: isPhoneRuntime(),
    isTablet: isTabletRuntime(),
    locale: getRuntimeLocale(),
    notificationToken: getNotificationToken(),
    platform: getRuntimePlatform(),
  };
}

export function resolveDeviceType(
  options: Pick<DeviceHeaderOptions, "kioskEmployeeId"> = {},
): DeviceType {
  const kioskEmployeeId = options.kioskEmployeeId ?? getKioskEmployeeId();
  if (kioskEmployeeId) {
    return "KIOSKO";
  }

  return isAppRuntime() ? "APP" : "WEB";
}

export function getTimezoneOffsetMinutes(): number {
  return new Date().getTimezoneOffset();
}

export function setLastKnownPosition(
  latitude?: number | null,
  longitude?: number | null,
): void {
  if (
    latitude == null ||
    longitude == null ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    lastKnownPosition = null;
    if (canUseWindow()) {
      delete window.__wktLastKnownPosition;
    }
    return;
  }

  lastKnownPosition = { latitude, longitude };
  if (canUseWindow()) {
    window.__wktLastKnownPosition = { latitude, longitude };
  }
}

export function getLastKnownPosition(): {
  latitude: number;
  longitude: number;
} | null {
  if (lastKnownPosition == null) {
    lastKnownPosition = readWindowLastKnownPosition();
  }

  return lastKnownPosition;
}

export function buildDeviceHeaders(
  options: DeviceHeaderOptions = {},
): Record<string, string> {
  const headers: Record<string, string> = {
    "X-DEVICE-TIMEZONE-OFFSET": String(getTimezoneOffsetMinutes()),
    "X-DEVICE-TYPE": resolveDeviceType({
      kioskEmployeeId: options.kioskEmployeeId,
    }),
  };

  const clientCompanyId = options.clientCompanyId ?? getClientCompanyId();
  if (clientCompanyId) {
    headers["X-CLIENT-COMPANY"] = clientCompanyId;
  }

  const kioskEmployeeId = options.kioskEmployeeId ?? getKioskEmployeeId();
  if (kioskEmployeeId) {
    headers["X-KIOSK-EMPLOYEE"] = kioskEmployeeId;
  }

  const deviceUuid = options.deviceUuid ?? getDeviceUuid();
  if (deviceUuid) {
    headers["X-DEVICE-UUID"] = deviceUuid;
  }

  if (lastKnownPosition) {
    headers["X-DEVICE-LATITUDE"] = String(lastKnownPosition.latitude);
    headers["X-DEVICE-LONGITUDE"] = String(lastKnownPosition.longitude);
  }

  return headers;
}
